// timeline-cursor.util.ts — the editor-dock helpers that read the timeline at a
// point in time: the ruler under the rows and "who is speaking at the playhead".

import type { TimelineLane } from './transcript-timing.util';

export interface RulerTick {
  sec: number;
  /** Position along the track, 0–100. */
  pct: number;
}

/** Whoever holds a lane at one instant, with the segment's bounds in seconds. */
export interface SpeakingNow {
  name: string;
  ai: boolean;
  at: number;
  end: number;
}

/** Round tick steps, chosen so a track never carries more than ~6 labels. */
const STEPS_SEC = [5, 10, 15, 30, 60, 120, 300, 600, 1200];
const MAX_TICKS = 6;

/** Tick marks for a track of `durationSec`: from 0 on a round step, inclusive of
 *  the last full step that fits. Empty for an unknown duration. */
export function rulerTicks(durationSec: number, zoom = 1): RulerTick[] {
  if (!(durationSec > 0)) return [];
  // Zoomed in, only durationSec / zoom is on screen at once — that is what the step must fit.
  const visible = durationSec / Math.max(1, zoom);
  const step = STEPS_SEC.find((s) => visible / s <= MAX_TICKS) ?? STEPS_SEC[STEPS_SEC.length - 1];
  const out: RulerTick[] = [];
  // One division, so 60 s of 350 s is exactly 60/3.5 and not a rounded product.
  for (let sec = 0; sec <= durationSec; sec += step) out.push({ sec, pct: (sec * 100) / durationSec });
  return out;
}

/** The first segment across `lanes` that contains `sec`, or null in a gap. */
export function segmentAt(lanes: TimelineLane[], sec: number): SpeakingNow | null {
  for (const lane of lanes) {
    const seg = lane.segs.find((g) => g.at <= sec && sec < g.end);
    if (seg) return { name: lane.name, ai: lane.ai, at: seg.at, end: seg.end };
  }
  return null;
}
